import { useState } from "react";
import { motion } from "framer-motion";
import { LogOut, Mail, Sparkles, User, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function AuthModal({ open, onClose, reason, wishlistCount = 0, cartCount = 0 }) {
  const { user, login, register, logout, forgotPassword, formatApiError } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      if (mode === "register") await register(form);
      else if (mode === "forgot") { const result = await forgotPassword(form.email); setMessage(result.message); }
      else await login({ email: form.email, password: form.password });
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <motion.div className="ns-overlay ns-center-overlay" data-testid="auth-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="ns-auth-modal" data-testid="auth-modal" initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.97 }}>
        <button className="ns-modal-close" data-testid="auth-close-button" onClick={onClose} aria-label="Close account"><X /></button>
        {user ? (
          <div className="ns-account-panel" data-testid="account-panel"><p className="ns-kicker">Your magical account</p><h2>Welcome back,<br /><em>{user.name}</em></h2><p><Mail size={15} /> {user.email}</p><div className="ns-account-stats"><span>{wishlistCount} wishlist</span><span>{cartCount} bag items</span></div><a className="ns-text-link" href="/account" data-testid="view-account-page-link">View account page</a>{user.role === "admin" && <a className="ns-text-link" href="/admin/orders" data-testid="admin-orders-link">Manage orders</a>}<button className="ns-button ns-button-ghost" data-testid="logout-button" onClick={logout}><LogOut size={16} /> Log out</button></div>
        ) : (
          <>
            <p className="ns-kicker"><Sparkles size={14} /> Customer account</p>
            <h2>{mode === "register" ? "Create your account" : mode === "forgot" ? "Reset password" : "Welcome back"}</h2>
            {reason && <p className="ns-auth-reason">{reason}</p>}
            <button className="ns-google-button" data-testid="google-login-button" onClick={googleLogin}><span>G</span> Continue with Google</button>
            <div className="ns-auth-divider"><span>or use email</span></div>
            <form onSubmit={submit}>
              {mode === "register" && <input data-testid="register-name-input" placeholder="Your name" value={form.name} onChange={(e) => update("name", e.target.value)} required />}
              {mode === "register" && <input data-testid="register-phone-input" type="tel" placeholder="Contact number" value={form.phone} onChange={(e) => update("phone", e.target.value)} required />}
              <input data-testid="auth-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => update("email", e.target.value)} required />
              {mode !== "forgot" && <input data-testid="auth-password-input" type="password" placeholder={mode === "register" ? "Create password (8+ characters)" : "Password"} value={form.password} onChange={(e) => update("password", e.target.value)} required />}
              {error && <p className="ns-form-error" data-testid="auth-error">{error}</p>}
              {message && <p className="ns-form-success" data-testid="auth-message">{message}</p>}
              <button className="ns-button ns-button-primary" data-testid="auth-submit-button" disabled={busy}>{busy ? "Please wait…" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset instructions" : "Log in"}</button>
            </form>
            <div className="ns-auth-switch">
              {mode !== "login" && <button data-testid="show-login-button" onClick={() => setMode("login")}>Log in</button>}
              {mode !== "register" && <button data-testid="show-register-button" onClick={() => setMode("register")}>Create account</button>}
              {mode !== "forgot" && <button data-testid="forgot-password-button" onClick={() => setMode("forgot")}>Forgot password?</button>}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
